/**
 * ReviewModalV2 - Question review modal with grid view
 *
 * Features:
 * - Grid of all questions with status
 * - Click to jump to question
 * - Shows answered, marked, unanswered counts
 * - Keyboard accessible
 */

import { memo, useCallback, useEffect, useRef } from 'react'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectProgress,
  selectMarkedCount
} from '../../../hooks/useChallenge'
import { createLogger } from '../../../utils/logger'

const logger = createLogger('ReviewModalV2')

// Status indicator colors (background only - markers are separate)
const statusColors = {
  answered: 'bg-emerald-500 text-white',
  unanswered: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
}

// Confidence level border colors (1-5)
const confidenceBorderColors = {
  1: 'ring-[3px] ring-red-500',
  2: 'ring-[3px] ring-orange-500',
  3: 'ring-[3px] ring-yellow-500',
  4: 'ring-[3px] ring-emerald-500',
  5: 'ring-[3px] ring-green-600'
}

const ReviewModalV2 = memo(() => {
  const modalRef = useRef(null)

  // Store state
  const showReviewModal = useChallengeStoreV2((state) => state.showReviewModal)
  const questions = useChallengeStoreV2((state) => state.questions)
  const questionStatuses = useChallengeStoreV2((state) => state.questionStatuses)
  const currentQuestionIndex = useChallengeStoreV2((state) => state.currentQuestionIndex)
  const currentStage = useChallengeStoreV2((state) => state.currentStage)
  const progress = useChallengeStoreV2(selectProgress)
  const markedCount = useChallengeStoreV2(selectMarkedCount)

  // For adaptive stages, use targetCount to show all question slots
  const totalSlots = currentStage?.targetCount || questions.length

  // Debug logging for duplicate key issue
  if (showReviewModal) {
    logger.debug('[ReviewModalV2] questionStatuses:', questionStatuses)
    logger.debug(
      '[ReviewModalV2] questions:',
      questions.map((q) => ({
        id: q.id || q.question_id || q.questionId,
        userAnswer: q.userAnswer
      }))
    )
  }

  // Store actions
  const closeReviewModal = useChallengeStoreV2((state) => state.closeReviewModal)
  const handleNavigateToQuestion = useChallengeStoreV2((state) => state.handleNavigateToQuestion)

  // Handle question click
  const handleQuestionClick = useCallback(
    (index) => {
      handleNavigateToQuestion(index)
      closeReviewModal()
    },
    [handleNavigateToQuestion, closeReviewModal]
  )

  // Handle escape key
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        closeReviewModal()
      }
    },
    [closeReviewModal]
  )

  // Focus trap and escape handling
  useEffect(() => {
    if (showReviewModal) {
      modalRef.current?.focus()
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [showReviewModal])

  if (!showReviewModal) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-modal-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeReviewModal} />

      {/* Modal */}
      <div
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-[modalIn_0.25s_ease-out]"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 id="review-modal-title" className="text-xl font-bold text-gray-900 dark:text-white">
              Question Review
            </h2>
            <button
              onClick={closeReviewModal}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
              <span className="text-gray-600 dark:text-gray-400">
                Answered: {progress.answered}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-500"></span>
              <span className="text-gray-600 dark:text-gray-400">Marked: {markedCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600"></span>
              <span className="text-gray-600 dark:text-gray-400">
                Unanswered: {progress.total - progress.answered}
              </span>
            </div>
          </div>

          {/* Confidence legend */}
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>Confidence:</span>
            <div className="flex items-center gap-3">
              <span className="w-4 h-4 rounded ring-2 ring-red-500 bg-gray-100"></span>
              <span className="w-4 h-4 rounded ring-2 ring-orange-500 bg-gray-100"></span>
              <span className="w-4 h-4 rounded ring-2 ring-yellow-500 bg-gray-100"></span>
              <span className="w-4 h-4 rounded ring-2 ring-emerald-500 bg-gray-100"></span>
              <span className="w-4 h-4 rounded ring-2 ring-green-600 bg-gray-100"></span>
            </div>
            <span className="text-gray-400">1-5</span>
          </div>
        </div>

        {/* Question Grid */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2">
            {Array.from({ length: totalSlots }, (_, index) => {
              const question = questions[index]
              const isLoaded = !!question
              const questionId = question?.id || question?.question_id || question?.questionId
              const status = questionId
                ? questionStatuses.find((s) => s.questionId === questionId)
                : null
              const isAnswered = status?.answered
              const isMarked = status?.marked
              const hasCrossedOut = status?.hasCrossedOut
              const isCurrent = index === currentQuestionIndex
              // Get confidence from question or status (confidence stored as 0-1, convert to 1-5)
              const rawConfidence = question?.confidence ?? status?.confidence
              const confidence = rawConfidence ? Math.round(rawConfidence * 5) : null

              // Background: answered (green), unanswered (gray), or not loaded yet (lighter gray with lock)
              let bgClass = statusColors.unanswered
              if (!isLoaded) {
                bgClass =
                  'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 border-2 border-dashed border-gray-300 dark:border-gray-600'
              } else if (isAnswered) {
                bgClass = statusColors.answered
              }

              return (
                <button
                  key={questionId || `slot-${index}`}
                  onClick={() => isLoaded && handleQuestionClick(index)}
                  disabled={!isLoaded}
                  className={`
                    relative w-full aspect-square rounded-lg font-semibold text-sm
                    transition-all duration-200 transform
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                    ${bgClass}
                    ${isLoaded ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed'}
                    ${isCurrent ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-gray-800' : ''}
                    ${!isCurrent && isLoaded && confidence && confidenceBorderColors[confidence] ? confidenceBorderColors[confidence] : ''}
                  `}
                  aria-label={`Question ${index + 1}${!isLoaded ? ', not yet loaded' : ''}${isAnswered ? ', answered' : ''}${isMarked ? ', marked for review' : ''}${hasCrossedOut ? ', has crossed out options' : ''}`}
                >
                  {index + 1}

                  {/* Lock icon for not-yet-loaded questions */}
                  {!isLoaded && (
                    <svg
                      className="absolute bottom-1 right-1 w-3 h-3 text-gray-400 dark:text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  )}

                  {/* Marked indicator dot - show when marked */}
                  {isMarked && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full border border-white dark:border-gray-800"></span>
                  )}

                  {/* CrossedOut indicator - red dot in TOP-LEFT corner (always shown when crossed out) */}
                  {hasCrossedOut && (
                    <span className="absolute -top-1 -left-1 w-3 h-3 bg-red-500 rounded-full border border-white dark:border-gray-800"></span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Click a question to jump to it
            </span>
            <button
              onClick={closeReviewModal}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
})

ReviewModalV2.displayName = 'ReviewModalV2'

export default ReviewModalV2
